/**
 * Counting what happens, without watching anybody.
 *
 * There is one question this exists to answer: of the people who paste a link, how many reach a
 * finished run, and where do the rest stop. That is not knowable by looking at the code, and
 * every judgement about what to build next is currently being made blind.
 *
 * What it does not do matters more than what it does. No identifier, no session, no cookie, no
 * address, no link, no song, no name, no score. A step happened, once, somewhere in the world.
 * The counts are per day and per step and that is the entire shape of the data, so there is
 * nothing in here that could be turned back into a person even by somebody who wanted to.
 *
 * The consequence of keeping it that thin is that it cannot follow one person through the funnel,
 * so the numbers are ratios between steps rather than a true cohort. That is the right trade:
 * knowing roughly where people stop is worth having, and knowing who they were is not.
 */

/**
 * The only things worth counting.
 *
 * A closed list on both sides. The worker refuses anything not on its own copy, so a page that
 * has been tampered with cannot fill the store with whatever it likes.
 */
export type Step =
  | "arrive"
  | "paste"
  | "forge-start"
  | "forge-done"
  | "forge-failed"
  | "play-open"
  | "play-start"
  | "play-finish"
  | "score-posted"
  | "clip-made"
  | "shelf-open"
  | "set-open"
  | "set-shared"
  | "made-shared";

/** Sent in batches on the way out, so counting never costs a request during play. */
const pending: Step[] = [];
let scheduled = false;

let endpoint = "";
let enabled = false;

export function startCounting(base: string): void {
  endpoint = `${base.replace(/\/$/, "")}/count`;
  // Honour the browser-level ask not to be tracked, even though there is nothing here to track.
  const asked = (navigator as { doNotTrack?: string }).doNotTrack;
  enabled = asked !== "1" && endpoint !== "/count";
}

export function count(step: Step): void {
  if (!enabled) return;
  pending.push(step);
  if (scheduled) return;
  scheduled = true;
  // Idle rather than immediate: nothing here is worth a millisecond of a frame during a run.
  setTimeout(flush, 4000);
}

function flush(): void {
  scheduled = false;
  if (pending.length === 0) return;
  const body = JSON.stringify({ steps: pending.splice(0, pending.length) });
  try {
    /*
     * `keepalive` rather than `sendBeacon`, and text rather than JSON.
     *
     * Both of those were learned from the thing failing. A beacon is always sent with
     * credentials, so it needs the other end to say it accepts credentialed requests, and
     * saying that means cookies now cross to a service that has no business seeing them, to buy
     * nothing. `keepalive` survives the page closing just as well without asking for any of it.
     *
     * Text, because a cross-origin POST of `application/json` is preflighted and a request made
     * while the page is closing does not get to wait for the answer to a preflight. Plain text
     * is one of the handful of types the browser sends straight out. The body is still JSON;
     * only the label changes.
     */
    void fetch(endpoint, {
      method: "POST",
      body,
      keepalive: true,
      credentials: "omit",
      headers: { "content-type": "text/plain" },
    }).catch(() => undefined);
  } catch {
    // Counting is never worth breaking anything over.
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
